import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Copy, Plus } from "lucide-react";
import { useTranslations } from "use-intl";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { showError } from "@/lib/toast";
import { useWorkflowWebhooks } from "@/hooks/api/queries/workflows.queries";
import {
  useCreateWorkflowWebhook,
  useDeleteWorkflowWebhook,
  useRotateWorkflowWebhookSecret,
  useUpdateWorkflowWebhook,
} from "@/hooks/api/mutations/workflows.mutations";
import type { Workflow, WorkflowWebhookTrigger } from "@/types/workflow.types";
import { WebhookTriggerRow } from "./webhook-trigger-row";

interface WebhooksTabProps {
  workflow: Workflow;
}

export default function WebhooksTab({ workflow }: WebhooksTabProps) {
  const t = useTranslations("workflows");
  const { data, isLoading } = useWorkflowWebhooks(workflow.id);
  const createWebhook = useCreateWorkflowWebhook();
  const updateWebhook = useUpdateWorkflowWebhook();
  const rotateSecret = useRotateWorkflowWebhookSecret();
  const deleteWebhook = useDeleteWorkflowWebhook();
  const [name, setName] = useState("");
  const [secret, setSecret] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const triggers = data?.data ?? [];
  const canWrite =
    !workflow.effectiveActions ||
    workflow.effectiveActions.includes("write") ||
    workflow.effectiveActions.includes("admin");
  const hasRunAs = workflow.runAsPrincipalKind === "user" && !!workflow.runAsPrincipalId;

  const publicPath = useMemo(() => "/v1/workflow-webhooks/", []);

  const handleCreate = (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !canWrite || !hasRunAs) return;
    createWebhook.mutate(
      { workflowId: workflow.id, data: { name: name.trim(), enabled: true } },
      {
        onSuccess: (res) => {
          setName("");
          setSecret(res.data.secret ?? null);
          toast.success(t("webhooks.toast_created"));
        },
        onError: (err) =>
          showError((err as { message?: string }).message || t("webhooks.toast_failed")),
      },
    );
  };

  const copy = async (value: string, message: string) => {
    await navigator.clipboard.writeText(value);
    toast.success(message);
  };

  const toggle = (trigger: WorkflowWebhookTrigger) => {
    updateWebhook.mutate(
      { workflowId: workflow.id, triggerId: trigger.id, data: { enabled: !trigger.enabled } },
      {
        onSuccess: () =>
          toast.success(
            trigger.enabled ? t("webhooks.toast_disabled") : t("webhooks.toast_enabled"),
          ),
        onError: (err) =>
          showError((err as { message?: string }).message || t("webhooks.toast_failed")),
      },
    );
  };

  const rotate = (trigger: WorkflowWebhookTrigger) => {
    rotateSecret.mutate(
      { workflowId: workflow.id, triggerId: trigger.id },
      {
        onSuccess: (res) => {
          setSecret(res.data.secret ?? null);
          toast.success(t("webhooks.toast_rotated"));
        },
        onError: (err) =>
          showError((err as { message?: string }).message || t("webhooks.toast_failed")),
      },
    );
  };

  const remove = (trigger: WorkflowWebhookTrigger) => {
    deleteWebhook.mutate(
      { workflowId: workflow.id, triggerId: trigger.id },
      {
        onSuccess: () => {
          setConfirmDeleteId(null);
          toast.success(t("webhooks.toast_deleted"));
        },
        onError: (err) =>
          showError((err as { message?: string }).message || t("webhooks.toast_failed")),
      },
    );
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-[var(--border)] bg-[var(--card)]">
        <div className="border-b border-[var(--border)] px-4 py-3">
          <h3 className="text-sm font-semibold text-[var(--foreground)]">{t("webhooks.title")}</h3>
          <p className="mt-1 text-xs text-[var(--muted-foreground)]">{t("webhooks.subtitle")}</p>
        </div>
        <form onSubmit={handleCreate} className="grid gap-3 p-4 md:grid-cols-[minmax(0,1fr)_auto]">
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("webhooks.name_placeholder")}
            className="h-10 min-w-0 rounded-md border border-[var(--border)] bg-[var(--background)] px-3 text-sm text-[var(--foreground)] outline-none focus:border-[var(--primary)]"
            disabled={!canWrite || !hasRunAs || createWebhook.isPending}
          />
          <Button
            type="submit"
            variant="primary"
            size="sm"
            startIcon={<Plus className="size-4" />}
            isLoading={createWebhook.isPending}
            disabled={!name.trim() || !canWrite || !hasRunAs}
          >
            {t("webhooks.create")}
          </Button>
        </form>
        {!hasRunAs && (
          <p className="px-4 pb-4 text-xs text-amber-600">{t("webhooks.run_as_required")}</p>
        )}
        {!canWrite && (
          <p className="px-4 pb-4 text-xs text-[var(--muted-foreground)]">
            {t("webhooks.write_required")}
          </p>
        )}
      </section>

      {secret && (
        <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 dark:border-emerald-800 dark:bg-emerald-950/30">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                {t("webhooks.secret_ready")}
              </p>
              <p className="mt-1 break-all font-mono text-xs text-emerald-800 dark:text-emerald-200">
                {secret}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              startIcon={<Copy className="size-4" />}
              onClick={() => copy(secret, t("webhooks.toast_copied"))}
            >
              {t("webhooks.copy_secret")}
            </Button>
          </div>
        </section>
      )}

      <div className="space-y-2">
        {isLoading && (
          <p className="py-8 text-center text-sm text-[var(--muted-foreground)]">
            {t("webhooks.loading")}
          </p>
        )}
        {!isLoading && triggers.length === 0 && (
          <p className="rounded-lg border border-dashed border-[var(--border)] py-8 text-center text-sm text-[var(--muted-foreground)]">
            {t("webhooks.empty")}
          </p>
        )}
        {triggers.map((trigger) => (
          <WebhookTriggerRow
            key={trigger.id}
            trigger={trigger}
            publicPath={publicPath}
            confirmDelete={confirmDeleteId === trigger.id}
            busy={updateWebhook.isPending || rotateSecret.isPending || deleteWebhook.isPending}
            canWrite={canWrite}
            onCopy={copy}
            onToggle={() => toggle(trigger)}
            onRotate={() => rotate(trigger)}
            onDelete={() =>
              confirmDeleteId === trigger.id ? remove(trigger) : setConfirmDeleteId(trigger.id)
            }
            onCancelDelete={() => setConfirmDeleteId(null)}
          />
        ))}
      </div>
    </div>
  );
}
